/**
 * Mobile-specific utilities for enhanced mobile experience
 */

// Device detection utilities
export const deviceDetection = {
  // Check if device is mobile
  isMobile: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  },

  // Check if device is iOS
  isIOS: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  },

  // Check if device is Android
  isAndroid: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return /Android/.test(navigator.userAgent);
  },

  // Check if device supports touch
  isTouchDevice: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  },

  // Check if device has notch (safe area)
  hasNotch: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    // Check if CSS safe area is supported
    return CSS.supports('padding-top: env(safe-area-inset-top)');
  },

  // Get device pixel ratio
  getPixelRatio: (): number => {
    if (typeof window === 'undefined') return 1;
    
    return window.devicePixelRatio || 1;
  },

  // Check if device prefers reduced motion
  prefersReducedMotion: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  },
};

// Viewport utilities
export const viewport = {
  // Get current viewport dimensions
  getDimensions: () => {
    if (typeof window === 'undefined') {
      return { width: 0, height: 0 };
    }
    
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  },

  // Get safe area insets (for notched devices)
  getSafeAreaInsets: () => {
    if (typeof window === 'undefined' || !CSS.supports('padding-top: env(safe-area-inset-top)')) {
      return { top: 0, right: 0, bottom: 0, left: 0 };
    }

    // Get computed safe area values
    const computedStyle = getComputedStyle(document.documentElement);
    
    return {
      top: parseInt(computedStyle.getPropertyValue('--safe-area-inset-top') || '0'),
      right: parseInt(computedStyle.getPropertyValue('--safe-area-inset-right') || '0'),
      bottom: parseInt(computedStyle.getPropertyValue('--safe-area-inset-bottom') || '0'),
      left: parseInt(computedStyle.getPropertyValue('--safe-area-inset-left') || '0'),
    };
  },

  // Check if viewport is in landscape mode
  isLandscape: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    return window.innerWidth > window.innerHeight;
  },

  // Get viewport height accounting for mobile browsers
  getViewportHeight: (): number => {
    if (typeof window === 'undefined') return 0;
    
    // Use visualViewport if available (accounts for mobile browser UI)
    if (window.visualViewport) {
      return window.visualViewport.height;
    }
    
    return window.innerHeight;
  },
};

// Haptic feedback utilities
export const haptics = {
  // Trigger haptic feedback (iOS Safari and some Android browsers)
  vibrate: (pattern: number | number[] = 10): boolean => {
    if (typeof window === 'undefined' || !navigator.vibrate) {
      return false;
    }
    
    try {
      navigator.vibrate(pattern);
      return true;
    } catch {
      return false;
    }
  },

  // Light haptic feedback
  light: (): boolean => haptics.vibrate(10),

  // Medium haptic feedback
  medium: (): boolean => haptics.vibrate(20),

  // Heavy haptic feedback
  heavy: (): boolean => haptics.vibrate(30),

  // Success haptic pattern
  success: (): boolean => haptics.vibrate([10, 50, 10]),

  // Error haptic pattern
  error: (): boolean => haptics.vibrate([20, 100, 20, 100, 20]),

  // Selection haptic feedback
  selection: (): boolean => haptics.vibrate(5),
};

// Touch utilities
export const touch = {
  // Prevent default touch behaviors
  preventScroll: (element: HTMLElement): void => {
    element.style.touchAction = 'none';
  },

  // Allow vertical scrolling only
  allowVerticalScroll: (element: HTMLElement): void => {
    element.style.touchAction = 'pan-y';
  },

  // Allow horizontal scrolling only
  allowHorizontalScroll: (element: HTMLElement): void => {
    element.style.touchAction = 'pan-x';
  },

  // Reset touch action
  resetTouchAction: (element: HTMLElement): void => {
    element.style.touchAction = 'auto';
  },

  // Check if touch event is a swipe
  isSwipe: (
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    threshold = 50
  ): {
    isSwipe: boolean;
    direction: 'left' | 'right' | 'up' | 'down' | null;
  } => {
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    if (Math.max(absDeltaX, absDeltaY) < threshold) {
      return { isSwipe: false, direction: null };
    }

    if (absDeltaX > absDeltaY) {
      return {
        isSwipe: true,
        direction: deltaX > 0 ? 'right' : 'left',
      };
    } else {
      return {
        isSwipe: true,
        direction: deltaY > 0 ? 'down' : 'up',
      };
    }
  },
};

// Performance utilities
export const performance = {
  now: (): number => Date.now(),

  // Check if device is low-end (for performance optimization)
  isLowEndDevice: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    // Check device memory (Chrome only)
    const deviceMemory = (navigator as any).deviceMemory;
    if (deviceMemory && deviceMemory <= 2) {
      return true;
    }
    
    // Check hardware concurrency (number of CPU cores)
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      return true;
    }
    
    // Check connection type (if available)
    const connection = (navigator as any).connection;
    if (connection && (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g')) {
      return true;
    }
    
    return false;
  },

  isBatteryLow: async (): Promise<boolean> => {
    if (typeof navigator === 'undefined' || !('getBattery' in navigator)) {
      return false;
    }

    try {
      const battery = await (navigator as any).getBattery();
      return !battery.charging && battery.level <= 0.2;
    } catch {
      return false;
    }
  },

  // Request idle callback with fallback
  requestIdleCallback: (callback: () => void, timeout = 5000): number => {
    if (typeof window === 'undefined') return 0;
    
    if ('requestIdleCallback' in window) {
      return (window as any).requestIdleCallback(callback, { timeout });
    }
    
    // Fallback for browsers without requestIdleCallback
    return setTimeout(callback, 1) as unknown as number;
  },

  // Cancel idle callback with fallback
  cancelIdleCallback: (id: number): void => {
    if (typeof window === 'undefined') return;
    
    if ('cancelIdleCallback' in window) {
      (window as any).cancelIdleCallback(id);
    } else {
      clearTimeout(id);
    }
  },
};

// Network utilities
export const network = {
  // Get connection information
  getConnectionInfo: () => {
    if (typeof window === 'undefined') return null;
    
    const connection = (navigator as any).connection;
    if (!connection) return null;
    
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
    };
  },

  // Check if user has data saver enabled
  isDataSaverEnabled: (): boolean => {
    if (typeof window === 'undefined') return false;
    
    const connection = (navigator as any).connection;
    return connection?.saveData || false;
  },

  // Check if connection is slow
  isSlowConnection: (): boolean => {
    const info = network.getConnectionInfo();
    if (!info) return false;
    
    return info.effectiveType === 'slow-2g' || info.effectiveType === '2g';
  },
};

// Safe area utilities
export const safeArea = {
  // Set CSS custom properties for safe area insets
  setCSSCustomProperties: (): void => {
    if (typeof window === 'undefined' || !CSS.supports('padding-top: env(safe-area-inset-top)')) {
      return;
    }

    // Set up CSS custom properties that can be used as fallbacks
    const root = document.documentElement;
    
    // Create a temporary element to compute safe area values
    const testElement = document.createElement('div');
    testElement.style.cssText = `
      position: fixed;
      top: env(safe-area-inset-top);
      right: env(safe-area-inset-right);
      bottom: env(safe-area-inset-bottom);
      left: env(safe-area-inset-left);
      visibility: hidden;
      pointer-events: none;
    `;
    
    document.body.appendChild(testElement);
    
    const computedStyle = getComputedStyle(testElement);
    
    root.style.setProperty('--safe-area-inset-top', computedStyle.top || '0px');
    root.style.setProperty('--safe-area-inset-right', computedStyle.right || '0px');
    root.style.setProperty('--safe-area-inset-bottom', computedStyle.bottom || '0px');
    root.style.setProperty('--safe-area-inset-left', computedStyle.left || '0px');
    
    document.body.removeChild(testElement);
  },

  // Initialize safe area handling
  initialize: (): void => {
    if (typeof window === 'undefined') return;

    // Set initial CSS custom properties
    safeArea.setCSSCustomProperties();
    
    // Update on orientation change
    const handleOrientationChange = () => {
      // Small delay to allow browser to update viewport
      setTimeout(() => {
        safeArea.setCSSCustomProperties();
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // Set dynamic viewport height for mobile browsers
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', () => {
      setTimeout(setVH, 100);
    });
  },

  // Check if safe area is supported
  isSupported: (): boolean => {
    return CSS.supports('padding-top: env(safe-area-inset-top)');
  },
};

// Utility to combine all mobile optimizations
export const mobileOptimizations = {
  // Get recommended settings based on device capabilities
  getRecommendedSettings: () => {
    const isLowEnd = performance.isLowEndDevice();
    const isSlowNet = network.isSlowConnection();
    const prefersReduced = deviceDetection.prefersReducedMotion();

    return {
      // Animation settings
      enableAnimations: !prefersReduced && !isLowEnd,
      enableKenBurns: !prefersReduced && !isLowEnd,
      animationDuration: isLowEnd ? 0.3 : 0.6,

      // Image settings
      useHighQuality: !isLowEnd && !isSlowNet && !network.isDataSaverEnabled(),
      enableLazyLoading: true,
      preloadImages: !isSlowNet && !network.isDataSaverEnabled(),

      // Performance settings
      enableHapticFeedback: deviceDetection.isTouchDevice(),
      reducedEffects: isLowEnd || prefersReduced,
    };
  },
};
